/**
 * IcgResourceExplainer — explainer for ICG-St, the RESOURCE Immunity
 * Closure Guarantee (governance name: RESOURCE Safety Envelope).
 *
 * ICG-St pairs the RES.BALANCE Dual Pair Guarantee (primal) with the
 * ACCOUNT Structural Duality Principle (dual) along the (−⊗A) ⊣ (A*⊗−)
 * compact-closed adjunction. The joint object is a double-entry resource
 * object whose unit (coevaluation) and counit (evaluation) are tied by the
 * snake equations: every acquisition is sealed to exactly one release.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import {
  AuditSurfaceDelta,
  CalloutBox,
  CounterModelGrid,
  ExplainerCard,
  SafetyEnvelopeDiagram,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";
import {
  COMPOSITIONALITY_PRINCIPLE_COLOR,
  DUAL_PAIR_COLOR,
  THEOREM_COLOR,
} from "../../design-system/tokens.js";
import { MathInline, tex } from "../../components/math/index.js";

const PRIMAL_ACCENT = DUAL_PAIR_COLOR["RES.BALANCE"].color;
const DUAL_ACCENT = COMPOSITIONALITY_PRINCIPLE_COLOR.ACCOUNT.color;

const COUNTER_MODELS = [
  {
    title: "Primal only — RES.BALANCE, no ACCOUNT",
    body: "Consume-within-budget and release-on-exit hold inside one regime, but stacking two resource accounts across a refactor is unwitnessed: nothing certifies the joint account is itself a RES.BALANCE witness rather than a weaker one — orphaned obligations can re-emerge at the seam.",
  },
  {
    title: "Dual only — ACCOUNT, no RES.BALANCE",
    body: "The linear ⊣ compact-closed duality composes, but with no balance neutralization there is no acquisition/release pairing to compose — ACCOUNT certifies the stacking of an empty guarantee.",
  },
] as const;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-icg-st",
  kind: "SSM · Immunity Closure Guarantee (S1⊗S2)",
  title: "ICG-St",
  subtitle: "RESOURCE Safety Envelope · RES.BALANCE ⊗ ACCOUNT",
  accent: PRIMAL_ACCENT,
  accentAlt: DUAL_ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  {
    label: "RES.BALANCE (primal DPG)",
    href: "#explainer-res-balance",
    accent: PRIMAL_ACCENT,
  },
  { label: "ACCOUNT (dual SDP)", href: "#explainer-account", accent: DUAL_ACCENT },
  {
    label: "RES.CONSUME (R1)",
    href: "#explainer-res-consume",
    accent: THEOREM_COLOR["RES.CONSUME"],
  },
  {
    label: "RES.RELEASE (R2)",
    href: "#explainer-res-release",
    accent: THEOREM_COLOR["RES.RELEASE"],
  },
];

const ENVELOPE = (
  <SafetyEnvelopeDiagram
    icg="ICG-St"
    primalLabel="RES.BALANCE"
    primalKind="Dual Pair Guarantee · R1 + R2"
    primalAccent={PRIMAL_ACCENT}
    dualLabel="ACCOUNT"
    dualKind="Structural Duality Principle · St"
    dualAccent={DUAL_ACCENT}
    adjunction="(−⊗A) ⊣ (A*⊗−)"
    unitRole="coevaluation (name / cup)"
    counitRole="evaluation (cap)"
  />
);

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      <strong>ICG-St</strong> is the RESOURCE Safety Envelope. It seals resource neutralization
      (RES.BALANCE) and its compositional dual (ACCOUNT) into one two-sided certificate —
      double-entry accounting as a single audited object.
    </p>
  ),
  intuition: (
    <>
      <p>
        RES.BALANCE makes double-spends and leaks unconstructable: a token consumed twice and a
        resource released without being acquired are both ill-typed. ACCOUNT certifies that the
        linear ↔ compact-closed duality preserves that guarantee when accounts are stacked.
      </p>
      <p>
        Held separately, the coherence that the balanced account in one regime <em>is</em> the same
        account whose composition the SDP governs is an external argument. A wrong-envelope deputy
        satisfies each side against a different ledger.
      </p>
      <p>
        ICG-St is the single compact-closed object — a double-entry resource object — that contains
        both sides. Its cup and cap, sealed by the snake equations, are the structural proof that
        acquisition and release are complementary. The envelope is{" "}
        <strong>strictly stronger</strong> than either side alone.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        ICG-St is the compact-closed object carrying the adjunction{" "}
        <MathInline>{tex`(- \otimes A) \dashv (A^{*} \otimes -)`}</MathInline>. Its two structural
        witnesses are:
      </p>
      <ul>
        <li>
          the <strong>unit η</strong> — the coevaluation (the "name" or "cup"): it opens a release
          obligation for every acquisition;
        </li>
        <li>
          the <strong>counit ε</strong> — the evaluation (the "cap"): it discharges exactly one
          obligation.
        </li>
      </ul>
      <p>
        The triangle identities here are the <strong>snake equations</strong>: they force{" "}
        <MathInline>{tex`\eta`}</MathInline> and <MathInline>{tex`\varepsilon`}</MathInline> to
        zig-zag to the identity, so a resource whose release status is ambiguous is not a well-typed
        term. Each acquisition opens exactly one obligation; each release closes exactly one.
      </p>
      <AuditSurfaceDelta
        primalCheck="RES.BALANCE witness"
        dualCheck="ACCOUNT witness"
        envelopeLabel="ICG-St envelope"
        primalAccent={PRIMAL_ACCENT}
        dualAccent={DUAL_ACCENT}
      />
      <p style={{ marginTop: "0.5rem" }}>
        Categorical form: ICG-St is the <em>Adjunction Certificate</em> whose left adjoint is
        acquisition and whose right adjoint is release — the snake equations are the certificate
        that the two are mutual sections.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        <strong>Why "strictly stronger".</strong> Each side alone admits a counter-model the
        envelope rules out:
      </p>
      <CounterModelGrid counterModels={COUNTER_MODELS} ariaLabel="ICG-St counter-models" />
      <p style={{ marginTop: "0.75rem" }}>
        ICG-St makes the forbidden generator <strong>G4 (token fork)</strong> structurally absent:
        the linear structure denies duplication and the snake equations render an orphaned
        obligation ill-typed. The envelope enforces security axiom A4, Fail-Closed.
      </p>
      <p>
        <strong>Worked adversary.</strong> A capability token is consumed twice (a fork) or acquired
        and never released (a leak); a fabricated release with no matching acquisition is offered as
        a discharge. Under ICG-St the cup opens exactly one obligation per acquisition and the cap
        closes exactly one — a second consumer has no obligation to discharge, and a leak names an
        obligation no cap will close. The double-spend and the leak are both structurally
        inexpressible.
      </p>
      <p>
        <strong>Theorem-collision check.</strong> Bypassing ICG-St means bypassing RES.BALANCE and
        ACCOUNT at once, or escaping the compact-closed carrier — both leave the kernel category and
        vacate the guarantee rather than violate it.
      </p>
      <CalloutBox eyebrow="Safety envelope: ICG-St" accent={DUAL_ACCENT}>
        RES.BALANCE (DPG) ⊗ ACCOUNT (SDP) = ICG-St. The audit surface collapses from a DPG witness
        plus an SDP witness to one envelope carrying the compact-closed unit and counit.
      </CalloutBox>
    </>
  ),
  references: [
    {
      label: "Theorem Canon — Immunity Closure Guarantees (§7a)",
      path: "docs/reference/Theorem-Canon.md",
    },
    {
      label: "RD-97 — Immunity Closure Guarantees",
      path: "research/settled/RD-97-BI-GUARANTEES-TWO-SIDED-SAFETY.md",
      note: "Canonical source for the seven-envelope algebra and the naming triad.",
    },
    {
      label: "Safety Envelope foundations",
      path: "theory/structural/Immunity-Closure-Guarantees/00-Safety-Envelope-Foundations.md",
    },
    {
      label: "ICG-St theory document",
      path: "theory/structural/Immunity-Closure-Guarantees/03-ICG-St-Resource-Balance-And-Account.md",
    },
    {
      label: "Immunity Closure Guarantee (Agda)",
      path: "agda/Research/BiGuarantee/BiResource.agda",
    },
  ],
};

export function IcgResourceExplainer(): React.JSX.Element {
  return (
    <ExplainerCard identity={IDENTITY} tiers={TIERS} diagram={ENVELOPE} crossLinks={CROSS_LINKS} />
  );
}
